import React from 'react';
import { useSelector } from 'react-redux';
import { generateEventSourceByPageType } from '../../common/analytics/helpers/analyticsSourceHelper';
import {
  trackArticle,
  trackArticleFeedShowMore,
  trackArticleFeedTab,
  trackHeroArticle,
  trackHeroVideoArticle,
  trackMostPopularArticle,
  trackRecommendedLocationCard,
  trackRecommendedLocationCarouselArrow,
} from '../../common/analytics/analyticsTracker';
import { ANALYTICS_FEATURES } from '../../common/analytics/featureTypes';
import config from '../../common/config';
import { AdUnits } from '../../common/helpers/ads/dfp';
import { fetchLocationInsightLandingPageData } from '../../common/helpers/data/landingPages/fetchLocationInsightLandingPageData';
import { Content } from '../../common/helpers/data/landingPages/fetchPostsData';
import { fetchLandingPageTopContentData } from '../../common/helpers/data/landingPages/fetchTopContentData';
import { LandingPageTypes } from '../../common/helpers/domain/landingPages';
import {
  getSubcategories,
  PostMainCategory,
} from '../../common/helpers/domain/postCategories';
import logger from '../../common/helpers/logger/logger';
import { extractPostIds } from '../../components/articleFeed/articleFeedHelper';
import { getNoTabDefinition } from '../../components/articleFeed/articleFeedTab';
import { Breadcrumb } from '../../components/global/breadcrumb/Breadcrumb';
import { PostList } from '../../components/global/PostList/PostList';
import HeroArticle from '../../components/homePage/heroArticle/HeroArticle';
import HomeSeoHeader from '../../components/homePage/homeSeoHeader/HomeSeoHeader';
import { ModuleTitle } from '../../components/homePage/moduleTitle/ModuleTitle';
import { generatePostMainCategoryBreadcrumbList } from '../../components/landingPage/breadcrumb';
import BillboardAd from '../../components/landingPageAds/BillboardAd';
import MainTitle from '../../components/mainTitle/MainTitle';
import MostPopularPosts from '../../components/mostPopular/MostPopularPosts';
import {
  DynamicComponentForArticleFeedModule,
  DynamicComponentForFeaturedVideos,
  DynamicComponentForHeroVideo,
  DynamicComponentForInPageSearch,
  DynamicComponentForLocationRecommendation,
} from '../../components/pages/homePage/DynamicComponents';
import {
  AsideContainer,
  LeftContainer,
  TopContentWrapper,
} from '../../components/pages/homePage/HomePage.style';
import { homePageRowSetting } from '../../components/rows/homePageRowSetting';
import Row from '../../components/rows/Row';
import SeoFooter from '../../components/seoFooter/SeoFooter';
import Page from '../../layouts/main';
import { CustomNextPageContext } from '../../lib/customNextPageContext';
import commonResource from '../../locales/th/common.json';
import homeResource from '../../locales/th/home.json';
import { HeroArticleInfo } from '../../queries/homePage/heroArticles';
import { HeroVideoInfo } from '../../queries/homePage/heroVideo';
import { RecommendedLocation } from '../../queries/homePage/locationRecommendation';
import { MostPopular } from '../../queries/homePage/mostPopular';
import { WholeState } from '../../store/common/type';
import { updatePageType } from '../../store/home/slice';
import { updateCurrentSourcePage } from '../../store/search/slice';
import { getAdSectByPageType } from '../../common/helpers/ads/AdsHelper';

interface LocationInsightLandingPageProps {
  canonicalLink: string;
  heroArticle: HeroArticleInfo;
  popularArticles: MostPopular[];
  locationInsightPosts: Content[];
  zoneRecommendedLocations: RecommendedLocation[];
  trainlineRecommendedLocations: RecommendedLocation[];
  heroVideo: HeroVideoInfo;
  featuredVideos: Content[];
}

function LocationInsightLandingPage({
  canonicalLink,
  heroArticle,
  popularArticles,
  locationInsightPosts,
  zoneRecommendedLocations,
  trainlineRecommendedLocations,
  heroVideo,
  featuredVideos,
}: LocationInsightLandingPageProps) {
  const moduleResource = commonResource.postModules.locationInsight;
  const userInfo = useSelector((state: WholeState) =>
    state !== undefined ? state.common.user : {},
  );
  const analyticsSource = generateEventSourceByPageType(
    LandingPageTypes.LocationInsightPage,
  );

  return (
    <Page
      /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
      // @ts-ignore
      source={analyticsSource}
    >
      <HomeSeoHeader
        pageType={LandingPageTypes.LocationInsightPage}
        canonicalLink={canonicalLink}
      />
      <Breadcrumb
        items={generatePostMainCategoryBreadcrumbList(
          PostMainCategory.LocationInsight,
        )}
      />
      <Row verticalSpacing={0} maxWidth={homePageRowSetting.maxWidth}>
        <MainTitle
          title={homeResource.title.locationInsight}
          subtitle={homeResource.subtitle.locationInsight}
        />
      </Row>
      <Row
        verticalSpacing={0}
        maxWidth={homePageRowSetting.maxWidth}
        fullWidth={{ desktop: false, mobile: true, tablet: true }}
      >
        <TopContentWrapper>
          <LeftContainer>
            <HeroArticle
              heroArticleData={heroArticle}
              onClickHeroArticle={(heroArticle) =>
                trackHeroArticle(
                  userInfo,
                  heroArticle,
                  analyticsSource,
                  ANALYTICS_FEATURES.HERO_ARTICLE,
                )
              }
            />
          </LeftContainer>
          <AsideContainer>
            <MostPopularPosts
              post={popularArticles}
              onClick={(popularArticle) =>
                trackMostPopularArticle(
                  userInfo,
                  popularArticle,
                  analyticsSource,
                  ANALYTICS_FEATURES.POPULAR,
                )
              }
            />
          </AsideContainer>
        </TopContentWrapper>
      </Row>
      <Row fullWidth verticalSpacing={homePageRowSetting.vertical}>
        <BillboardAd
          adUnit={AdUnits.HP_Billboard1}
          sect={getAdSectByPageType(LandingPageTypes.LocationInsightPage)}
        />
      </Row>
      <Row
        verticalSpacing={homePageRowSetting.vertical}
        maxWidth={homePageRowSetting.maxWidth}
        fullWidth={{ desktop: false, mobile: true, tablet: true }}
      >
        <DynamicComponentForInPageSearch />
      </Row>
      {zoneRecommendedLocations.length > 0 && (
        <Row
          verticalSpacing={homePageRowSetting.vertical}
          maxWidth={homePageRowSetting.maxWidth}
        >
          <ModuleTitle
            title={
              homeResource.locationRecommendation.locationHighlight.zones.title
            }
            subtitle={
              homeResource.locationRecommendation.locationHighlight.zones
                .subtitle
            }
          />
          <DynamicComponentForLocationRecommendation
            locations={zoneRecommendedLocations}
            onClickRecommendedLocationCarouselArrow={() =>
              trackRecommendedLocationCarouselArrow(
                userInfo,
                analyticsSource,
                ANALYTICS_FEATURES.LOCATION_ZONE_HIGHLIGHT,
              )
            }
            onClickRecommendedLocationCard={(locationInfo) =>
              trackRecommendedLocationCard(
                userInfo,
                locationInfo,
                analyticsSource,
                ANALYTICS_FEATURES.LOCATION_ZONE_HIGHLIGHT,
              )
            }
          />
        </Row>
      )}
      <Row fullWidth verticalSpacing={homePageRowSetting.vertical}>
        <BillboardAd
          adUnit={AdUnits.HP_Billboard2}
          sect={getAdSectByPageType(LandingPageTypes.LocationInsightPage)}
        />
      </Row>
      {locationInsightPosts.length > 0 && (
        <Row
          verticalSpacing={homePageRowSetting.vertical}
          maxWidth={homePageRowSetting.maxWidth}
          dataTest="location-insight"
        >
          <ModuleTitle
            title={moduleResource.locationInsight.title}
            subtitle={moduleResource.locationInsight.subtitle}
          />
          <PostList
            contents={locationInsightPosts}
            adUnit={AdUnits.HP_Pos1}
            adSect={getAdSectByPageType(LandingPageTypes.LocationInsightPage)}
            onClickPostCard={(post) =>
              trackArticle(
                userInfo,
                post,
                analyticsSource,
                ANALYTICS_FEATURES.LATEST_LOCATION,
              )
            }
          />
        </Row>
      )}
      {trainlineRecommendedLocations.length > 0 && (
        <Row
          verticalSpacing={homePageRowSetting.vertical}
          maxWidth={homePageRowSetting.maxWidth}
        >
          <ModuleTitle
            title={
              homeResource.locationRecommendation.locationHighlight.trainlines
                .title
            }
            subtitle={
              homeResource.locationRecommendation.locationHighlight.trainlines
                .subtitle
            }
          />
          <DynamicComponentForLocationRecommendation
            locations={trainlineRecommendedLocations}
            onClickRecommendedLocationCarouselArrow={() =>
              trackRecommendedLocationCarouselArrow(
                userInfo,
                analyticsSource,
                ANALYTICS_FEATURES.LOCATION_TRAINELINE_HIGHLIGHT,
              )
            }
            onClickRecommendedLocationCard={(locationInfo) =>
              trackRecommendedLocationCard(
                userInfo,
                locationInfo,
                analyticsSource,
                ANALYTICS_FEATURES.LOCATION_TRAINELINE_HIGHLIGHT,
              )
            }
          />
        </Row>
      )}
      {(heroVideo || featuredVideos.length > 0) && (
        <Row fullWidth verticalSpacing={homePageRowSetting.vertical}>
          <div style={{ overflowX: 'hidden' }}>
            <DynamicComponentForHeroVideo
              heroVideo={heroVideo}
              onClick={(heroVideo) =>
                trackHeroVideoArticle(
                  userInfo,
                  heroVideo,
                  analyticsSource,
                  ANALYTICS_FEATURES.HERO_VIDEO,
                )
              }
            />
            <DynamicComponentForFeaturedVideos
              contents={featuredVideos}
              onClickVideoCard={(post) =>
                trackArticle(
                  userInfo,
                  post,
                  analyticsSource,
                  ANALYTICS_FEATURES.VIDEO,
                )
              }
            />
          </div>
        </Row>
      )}
      <Row fullWidth verticalSpacing={homePageRowSetting.vertical}>
        <BillboardAd
          adUnit={AdUnits.HP_Billboard3}
          sect={getAdSectByPageType(LandingPageTypes.LocationInsightPage)}
        />
      </Row>
      <Row
        verticalSpacing={homePageRowSetting.vertical}
        maxWidth={homePageRowSetting.maxWidth}
      >
        <ModuleTitle title={homeResource.readMore.title} />
        <DynamicComponentForArticleFeedModule
          tabDefinition={getNoTabDefinition(
            null,
            getSubcategories(PostMainCategory.LocationInsight),
          )}
          excludePostIds={extractPostIds(
            [...locationInsightPosts, ...featuredVideos],
            heroVideo,
          )}
          adUnit={AdUnits.HP_MrecInfiniteAd}
          adSect={getAdSectByPageType(LandingPageTypes.LocationInsightPage)}
          onClickPostCard={(post) =>
            trackArticle(
              userInfo,
              post,
              analyticsSource,
              ANALYTICS_FEATURES.ADDITIONAL,
            )
          }
          onClickArticleFeedShowMore={() => {
            trackArticleFeedShowMore(
              userInfo,
              analyticsSource,
              ANALYTICS_FEATURES.ADDITIONAL,
            );
          }}
          onClickArticleFeedTab={(selectedTab) => {
            trackArticleFeedTab(
              userInfo,
              analyticsSource,
              ANALYTICS_FEATURES.ADDITIONAL,
              selectedTab,
            );
          }}
        />
      </Row>
      <Row verticalSpacing={16} fullWidth>
        <SeoFooter />
      </Row>
    </Page>
  );
}

LocationInsightLandingPage.getInitialProps = async ({
  store,
  asPath,
  apolloClient,
}: CustomNextPageContext): Promise<LocationInsightLandingPageProps> => {
  const canonicalLink = config.baseUrl + asPath.substring(1);
  try {
    const { heroArticle, mostPopular, recommendedLocations } =
      await fetchLandingPageTopContentData(apolloClient, {
        postCategories: getSubcategories(PostMainCategory.LocationInsight),
      });

    const { locationInsight, video } =
      await fetchLocationInsightLandingPageData(
        apolloClient,
        heroArticle?.id ? [heroArticle.id] : [],
      );

    const landingPageType = LandingPageTypes.LocationInsightPage;
    store.dispatch(updatePageType(landingPageType));
    store.dispatch(
      updateCurrentSourcePage(generateEventSourceByPageType(landingPageType)),
    );

    const zoneRecommendedLocations = recommendedLocations.filter((item) => {
      if (
        item.locationType == 'zone' ||
        item.locationType == 'district' ||
        item.locationType == 'road'
      ) {
        return item;
      }
    });

    const trainlineRecommendedLocations = recommendedLocations.filter(
      (item) => {
        if (item.locationType == 'metro_station') {
          return item;
        }
      },
    );

    return {
      canonicalLink,
      heroArticle,
      popularArticles: mostPopular,
      locationInsightPosts: locationInsight,
      zoneRecommendedLocations,
      trainlineRecommendedLocations,
      heroVideo: video.heroVideo,
      featuredVideos: video.featuredVideos,
    };
  } catch (e) {
    logger.logError(
      'LocationInsightLandingPage.getInitialProps',
      'Error fetching data',
      e,
    );
  }
};

export default LocationInsightLandingPage;
